import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShieldCheckIcon, SparklesIcon, PaperclipIcon, ImageIcon, ArrowUpIcon, PackageSearchIcon, ReplyIcon, XIcon, MoreVerticalIcon, CheckIcon, CheckCheckIcon, ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
import { InventorySharePicker } from '../components/InventorySharePicker';
import { ListingCard } from '../components/ListingCard';
import { inventoryItems, InventoryItem } from '../data/inventory';
import { searchInventory, toListingCard } from '../utils/inventory';
type Presence = {
  state: 'online' | 'typing' | 'offline';
  lastSeen?: string;
};
type MsgStatus = 'sent' | 'delivered' | 'read';
interface Message {
  id: number;
  fromMe: boolean;
  text: string;
  time: string;
  status?: MsgStatus;
  // An inventory item shared into the thread via the "/" picker.
  share?: InventoryItem;
  // Id of the message in this thread that this one replies to.
  replyToId?: number;
}
const SHARED_RANGE_ROVER = inventoryItems.find(i => i.id === '4')!;
interface ListingInfo {
  title: string;
  price: string;
  image: string;
  specs: {
    label: string;
    value: string;
  }[];
}
interface Conversation {
  id: string;
  name: string;
  type: string;
  lastMessage: string;
  time: string;
  unread: number;
  avatar: string;
  listing: string | null;
  listingInfo?: ListingInfo;
  presence: Presence;
}
const mockConversations: Conversation[] = [{
  id: '1',
  name: 'Al Futtaim Automall',
  type: 'Verified Business',
  lastMessage: 'Yes, the car is available for viewing today.',
  time: '10:42 AM',
  unread: 2,
  avatar: 'A',
  listing: 'Toyota Prado GXR 2018',
  listingInfo: {
    title: 'Toyota Prado GXR 2018',
    price: 'AED 95,000',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=200&q=80',
    specs: [{
      label: 'Mileage',
      value: '78,000 km'
    }, {
      label: 'Transmission',
      value: 'Automatic'
    }, {
      label: 'Fuel',
      value: 'Petrol'
    }, {
      label: 'Location',
      value: 'Dubai, Al Quoz'
    }]
  },
  presence: {
    state: 'online'
  }
}, {
  id: '2',
  name: 'Talab AI Assistant',
  type: 'AI Agent',
  lastMessage: "I've found 3 new matches for your villa request.",
  time: 'Yesterday',
  unread: 0,
  avatar: 'T',
  listing: null,
  presence: {
    state: 'online'
  }
}, {
  id: '3',
  name: 'Ahmed M.',
  type: 'Individual',
  lastMessage: 'Can we negotiate the price?',
  time: 'Mon',
  unread: 0,
  avatar: 'AM',
  listing: 'MacBook Pro M2',
  listingInfo: {
    title: 'MacBook Pro M2 14"',
    price: 'AED 6,500',
    image: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=200&q=80',
    specs: [{
      label: 'Storage',
      value: '512 GB SSD'
    }, {
      label: 'Memory',
      value: '16 GB'
    }, {
      label: 'Condition',
      value: 'Like new'
    }, {
      label: 'Warranty',
      value: '6 months left'
    }]
  },
  presence: {
    state: 'offline',
    lastSeen: 'today at 9:14 AM'
  }
}];
const initialThreads: Record<string, Message[]> = {
  '1': [{
    id: 1,
    fromMe: true,
    text: 'Hi, is the Prado still available?',
    time: '10:32 AM',
    status: 'read'
  }, {
    id: 2,
    fromMe: false,
    text: 'Yes, the car is available for viewing today. We are open until 8 PM.',
    time: '10:34 AM'
  }, {
    id: 3,
    fromMe: true,
    text: 'Great — can I come by around 5 PM?',
    time: '10:36 AM',
    status: 'read',
    replyToId: 2
  }, {
    id: 4,
    fromMe: false,
    text: '5 PM works. Ask for Rashid at the front desk and he will bring the keys.',
    time: '10:37 AM',
    replyToId: 3
  }, {
    id: 5,
    fromMe: false,
    text: 'For reference: 78,000 km, GCC spec, full service history with us.',
    time: '10:39 AM'
  }, {
    id: 6,
    fromMe: true,
    text: 'Any accident history on it?',
    time: '10:41 AM',
    status: 'read',
    replyToId: 5
  }, {
    id: 7,
    fromMe: false,
    text: 'None at all — clean report. I can send the inspection PDF before you arrive.',
    time: '10:42 AM',
    replyToId: 6
  }],
  '2': [{
    id: 1,
    fromMe: false,
    text: "I've found 3 new matches for your villa request.",
    time: 'Yesterday'
  }],
  '3': [{
    id: 1,
    fromMe: false,
    text: 'Do you have something bigger than the Yaris for the weekend?',
    time: '9:02 AM'
  }, {
    id: 2,
    fromMe: true,
    text: 'Sure — this one is free from Friday:',
    time: '9:05 AM',
    status: 'read',
    share: SHARED_RANGE_ROVER,
    replyToId: 1
  }, {
    id: 3,
    fromMe: false,
    text: 'Looks great. Is the deposit refundable?',
    time: '9:09 AM',
    replyToId: 2
  }, {
    id: 4,
    fromMe: true,
    text: 'Yes — fully refundable within 5 working days of return.',
    time: '9:11 AM',
    status: 'read',
    replyToId: 3
  }, {
    id: 5,
    fromMe: false,
    text: 'Can we negotiate the price?',
    time: '9:14 AM'
  }]
};
function presenceLabel(p: Presence) {
  if (p.state === 'online') return {
    text: 'online',
    cls: 'text-green-600'
  };
  if (p.state === 'typing') return {
    text: 'typing…',
    cls: 'text-primary-indigo'
  };
  return {
    text: `last seen ${p.lastSeen ?? 'recently'}`,
    cls: 'text-slate-400'
  };
}
function Ticks({
  status,
  tone = 'light'
}: {
  status?: MsgStatus;
  tone?: 'light' | 'dark';
}) {
  if (!status) return null;
  const muted = tone === 'light' ? 'text-white/60' : 'text-slate-300';
  const read = tone === 'light' ? 'text-sky-300' : 'text-primary-indigo';
  if (status === 'sent') return <CheckIcon className={`w-3.5 h-3.5 ${muted}`} data-id="element-5648" data-mp="1yobipi8s5q0i" />;
  if (status === 'delivered') return <CheckCheckIcon className={`w-3.5 h-3.5 ${muted}`} data-id="element-5649" data-mp="2cnuqg9xoizca" />;
  return <CheckCheckIcon className={`w-3.5 h-3.5 ${read}`} data-id="element-5650" data-mp="27mnh46fw1s3i" />;
}
// The quoted original shown at the top of a reply (and in the composer banner).
function QuotedPreview({
  message,
  author,
  tone,
  onJump
}: {
  message: Message;
  author: string;
  tone: 'light' | 'dark';
  onJump?: () => void;
}) {
  const light = tone === 'light';
  const snippet = message.share ? `${message.share.title} · ${message.share.price}` : message.text || 'Shared listing';
  return <button type="button" onClick={onJump} className={`w-full flex items-stretch gap-2 rounded-lg overflow-hidden mb-1.5 text-left ${light ? 'bg-white/15 hover:bg-white/20' : 'bg-slate-100/80 hover:bg-slate-100'} transition-colors duration-150 ease-out`} data-id="element-5651" data-mp="2qg0vazvdtjwf">
      <span className={`w-[3px] flex-shrink-0 ${light ? 'bg-white/70' : 'bg-primary-indigo'}`} data-id="element-5652" data-mp="3jyffmwj3245y" />
      <span className="flex-1 min-w-0 py-1.5 pr-1" data-id="element-5653" data-mp="3nx517ce9ys0o">
        <span className={`block text-[11px] font-semibold ${light ? 'text-white/90' : 'text-primary-indigo'}`} data-id="element-5654" data-mp="39yeur65kffzn">
          {author}
        </span>
        <span className={`block text-xs truncate ${light ? 'text-white/75' : 'text-slate-500'}`} data-id="element-5655" data-mp="309omjcjxmvs7">
          {message.share && <PackageSearchIcon className="w-3 h-3 inline-block mr-1 -mt-0.5" data-id="element-5656" data-mp="3516jlpiu4c7w" />}
          {snippet}
        </span>
      </span>
      {message.share && <img src={message.share.image} alt="" className="w-9 h-9 object-cover rounded-md m-1.5 flex-shrink-0" data-id="element-5657" data-mp="2w7jswdthhz5k" />}
    </button>;
}
export function Messages() {
  const [activeChat, setActiveChat] = useState<Conversation>(mockConversations[0]);
  // On tablet/mobile we show either the inbox list OR the open chat (master-detail).
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [listingExpanded, setListingExpanded] = useState(false);
  const [threads, setThreads] = useState<Record<string, Message[]>>(initialThreads);
  const [draft, setDraft] = useState('');
  // "/" inventory picker
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pickerOpen = slashQuery !== null;
  const pickerResults = pickerOpen ? searchInventory(inventoryItems, slashQuery ?? '') : [];
  const messages = threads[activeChat.id] ?? [];
  const presence = presenceLabel(activeChat.presence);
  const quotedOf = (m: Message) => m.replyToId ? messages.find(x => x.id === m.replyToId) : undefined;
  const authorOf = (m: Message) => m.fromMe ? 'You' : activeChat.name;
  // Jump to the original message and flash it so the thread stays followable.
  const jumpTo = (id: number) => {
    const el = document.getElementById(`msg-${id}`);
    if (!el) return;
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    el.classList.add('ring-2', 'ring-primary-indigo/40', 'rounded-2xl');
    window.setTimeout(() => el.classList.remove('ring-2', 'ring-primary-indigo/40', 'rounded-2xl'), 1400);
  };
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight
    });
  }, [messages.length, activeChat.id]);
  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const id = Date.now();
    const time = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    setThreads(prev => ({
      ...prev,
      [activeChat.id]: [...(prev[activeChat.id] ?? []), {
        id,
        fromMe: true,
        text,
        time,
        status: 'sent',
        replyToId: replyingTo?.id
      }]
    }));
    setDraft('');
    setReplyingTo(null);
    // Simulate WhatsApp-style delivery → read progression.
    const update = (status: MsgStatus) => setThreads(prev => ({
      ...prev,
      [activeChat.id]: (prev[activeChat.id] ?? []).map(m => m.id === id ? {
        ...m,
        status
      } : m)
    }));
    window.setTimeout(() => update('delivered'), 700);
    window.setTimeout(() => update('read'), 1800);
  };
  // A trailing "/token" anywhere in the draft opens the inventory picker and
  // everything typed after the slash becomes the inventory search term.
  const syncSlashCommand = (value: string) => {
    const match = value.match(/(?:^|\s)\/([^/\s]*(?:\s[^/\s]*)?)$/);
    if (match) {
      setSlashQuery(match[1]);
      setActiveIndex(0);
    } else {
      setSlashQuery(null);
    }
  };
  const openPicker = () => {
    const next = draft.length === 0 || draft.endsWith(' ') ? `${draft}/` : `${draft} /`;
    setDraft(next);
    syncSlashCommand(next);
    inputRef.current?.focus();
  };
  const shareItem = (item: InventoryItem) => {
    const time = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    const id = Date.now();
    // Any text typed before the "/" is kept as the accompanying note.
    const note = draft.replace(/(?:^|\s)\/[^/]*$/, '').trim();
    setThreads(prev => ({
      ...prev,
      [activeChat.id]: [...(prev[activeChat.id] ?? []), {
        id,
        fromMe: true,
        text: note,
        time,
        status: 'sent',
        share: item,
        replyToId: replyingTo?.id
      }]
    }));
    setDraft('');
    setReplyingTo(null);
    setSlashQuery(null);
    inputRef.current?.focus();
    window.setTimeout(() => setThreads(prev => ({
      ...prev,
      [activeChat.id]: (prev[activeChat.id] ?? []).map(m => m.id === id ? {
        ...m,
        status: 'delivered' as MsgStatus
      } : m)
    })), 700);
  };
  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (pickerOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => pickerResults.length ? (i + 1) % pickerResults.length : 0);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => pickerResults.length ? (i - 1 + pickerResults.length) % pickerResults.length : 0);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const item = pickerResults[activeIndex];
        if (item) shareItem(item);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashQuery(null);
        return;
      }
    }
    if (e.key === 'Escape' && replyingTo) {
      e.preventDefault();
      setReplyingTo(null);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  };
  return <div className="flex-1 flex h-[calc(100vh-57px)] lg:h-[calc(100vh-80px)] bg-white overflow-hidden" data-id="element-5658" data-mp="232t2lmfpaodz">
      {/* Inbox List (Left) */}
      <div className={`${mobileView === 'chat' ? 'hidden' : 'flex'} w-full lg:flex lg:w-80 border-r border-slate-200 flex-col bg-slate-50/50`} data-id="element-5659" data-mp="1adltjtimegeh">
        <div className="p-4 border-b border-slate-200 bg-white" data-id="element-5660" data-mp="1oeg52l0ysxhd">
          <h1 className="font-display font-bold text-2xl text-dark-navy mb-4" data-id="element-5661" data-mp="1kb9yfc9xx4ad">
            Messages
          </h1>
          <div className="relative" data-id="element-5662" data-mp="2zqz0lqi4aija">
            <SearchIcon className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5663" data-mp="2zu4rnbab3uhn" />
            <input type="text" placeholder="Search messages..." className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border-transparent rounded-xl text-sm focus:bg-white focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/10 transition-all" data-id="element-5664" data-mp="2vt1a8kcm1qeb" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-5665" data-mp="8vig365dwwym">
          {mockConversations.map(chat => {
          const cp = presenceLabel(chat.presence);
          return <button key={chat.id} onClick={() => {
            setActiveChat(chat);
            setMobileView('chat');
            setListingExpanded(false);
            setReplyingTo(null);
          }} className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors flex gap-3 ${activeChat.id === chat.id ? 'bg-primary-indigo/5 border-l-4 border-l-primary-indigo' : 'border-l-4 border-l-transparent'}`} data-id="element-5666" data-mp="hvx3qc5tmo5a">
                <div className="relative flex-shrink-0" data-id="element-5667" data-mp="2m28b5cepsp7f">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${chat.type === 'AI Agent' ? 'bg-gradient-to-br from-primary-indigo to-ai-purple' : 'bg-slate-300'}`} data-id="element-5668" data-mp="2lzlw6rphv7f6">
                    {chat.type === 'AI Agent' ? <SparklesIcon className="w-6 h-6" data-id="element-5669" data-mp="34sg9zibdstht" /> : chat.avatar}
                  </div>
                  {chat.presence.state === 'online' && <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" data-id="element-5670" data-mp="2v24ssu2osnsz" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-5671" data-mp="2q1pikbdpbwf8">
                  <div className="flex justify-between items-baseline mb-1" data-id="element-5672" data-mp="3njzbtlto61i9">
                    <h3 className="font-bold text-slate-900 truncate pr-2" data-id="element-5673" data-mp="3irpgfq9sc98l">
                      {chat.name}
                    </h3>
                    <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-5674" data-mp="1gw05h9jfhkl0">
                      {chat.time}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs mb-1" data-id="element-5675" data-mp="175duxtqkjk96">
                    {chat.type === 'Verified Business' && <ShieldCheckIcon className="w-3.5 h-3.5 text-success" data-id="element-5676" data-mp="1c2dzg07rsey6" />}
                    <span className={chat.type === 'AI Agent' ? 'text-ai-purple font-medium' : 'text-slate-500'} data-id="element-5677" data-mp="y2metgmr00b7">
                      {chat.type}
                    </span>
                  </div>
                  <p className={`text-sm truncate ${chat.unread > 0 ? 'font-semibold text-slate-900' : 'text-slate-500'}`} data-id="element-5678" data-mp="2ik14i9zc3kp3">
                    {chat.lastMessage}
                  </p>
                </div>
                {chat.unread > 0 && <div className="w-5 h-5 bg-primary-indigo text-white text-xs font-bold flex items-center justify-center rounded-full mt-1 flex-shrink-0" data-id="element-5679" data-mp="btnxn9bn8zcw">
                    {chat.unread}
                  </div>}
              </button>;
        })}
        </div>
      </div>

      {/* Conversation Panel (Right) */}
      <div className={`${mobileView === 'chat' ? 'flex' : 'hidden'} lg:flex flex-1 flex-col bg-white min-w-0`} data-id="element-5680" data-mp="ulgvpi5klarh">
        {/* Chat Header */}
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-5681" data-mp="ppjdt7enrwcl">
          <div className="flex items-center gap-3" data-id="element-5682" data-mp="14i3em1ec5rqv">
            <button onClick={() => setMobileView('list')} aria-label="Back to messages" className="lg:hidden -ml-1 p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0" data-id="element-5683" data-mp="zmed3sira0rx">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-5684" data-mp="2fjk3gk9gv3gy" />
            </button>
            <div className="relative" data-id="element-5685" data-mp="2fjacouglksa7">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${activeChat.type === 'AI Agent' ? 'bg-gradient-to-br from-primary-indigo to-ai-purple' : 'bg-slate-300'}`} data-id="element-5686" data-mp="2kidjokg518kt">
                {activeChat.type === 'AI Agent' ? <SparklesIcon className="w-5 h-5" data-id="element-5687" data-mp="1wky3j8poriem" /> : activeChat.avatar}
              </div>
              {activeChat.presence.state === 'online' && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-id="element-5688" data-mp="26iceissubezj" />}
            </div>
            <div data-id="element-5689" data-mp="2aicuihvl7y8p">
              <div className="flex items-center gap-1.5" data-id="element-5690" data-mp="3hy14lt1e4u4k">
                <h2 className="font-bold text-slate-900" data-id="element-5691" data-mp="3hxhli9007zoy">{activeChat.name}</h2>
                {activeChat.type === 'Verified Business' && <ShieldCheckIcon className="w-4 h-4 text-success" data-id="element-5692" data-mp="1ac9disbd4unl" />}
              </div>
              <span className={`text-xs font-medium ${presence.cls}`} data-id="element-5693" data-mp="16axlkg50nxc8">
                {presence.text}
              </span>
            </div>
          </div>
          <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors" data-id="element-5694" data-mp="11ie5nciuwq49">
            <MoreVerticalIcon className="w-5 h-5" data-id="element-5695" data-mp="wkm5jv825xoz" />
          </button>
        </div>

        {/* Product banner (if listing attached) — expandable to reveal details */}
        {activeChat.listingInfo && <div className="bg-slate-50 border-b border-slate-200" data-id="element-5696" data-mp="mldi7l7p7hbl">
            <button onClick={() => setListingExpanded(v => !v)} aria-expanded={listingExpanded} className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-100/70 transition-colors" data-id="element-5697" data-mp="im4lc7hd33bt">
              <img src={activeChat.listingInfo.image} alt={activeChat.listingInfo.title} className="w-11 h-11 rounded-lg object-cover flex-shrink-0 border border-slate-200" data-id="element-5698" data-mp="2cwrq07eeinfj" />
              <div className="flex-1 min-w-0" data-id="element-5699" data-mp="2wb9cht5k2c18">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium" data-id="element-5700" data-mp="2wdm1gq9qdmns">
                  Regarding
                </p>
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-5701" data-mp="2mmhp7ybfwfwn">
                  {activeChat.listingInfo.title}
                </p>
              </div>
              <span className="text-sm font-bold text-primary-indigo whitespace-nowrap flex-shrink-0" data-id="element-5702" data-mp="2hka9cxnhofp1">
                {activeChat.listingInfo.price}
              </span>
              <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${listingExpanded ? 'rotate-180' : ''}`} data-id="element-5703" data-mp="28mgl89zvoscy" />
            </button>
            <AnimatePresence initial={false} data-id="element-5704" data-mp="6u1ymxme2zq7">
              {listingExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className="overflow-hidden" data-id="element-5705" data-mp="1wsbjomh305j">
                  <div className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-5706" data-mp="3rkcxk0mn9t8y">
                    {activeChat.listingInfo.specs.map(s => <div key={s.label} className="flex justify-between items-baseline border-b border-slate-200/70 pb-1.5" data-id="element-5707" data-mp="dimd9y878e5">
                        <span className="text-xs text-slate-500" data-id="element-5708" data-mp="1bsp3ccz0metu">
                          {s.label}
                        </span>
                        <span className="text-xs font-medium text-slate-800" data-id="element-5709" data-mp="1guo3ldzgkvmk">
                          {s.value}
                        </span>
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        {/* Chat Messages Area */}
        <div ref={scrollRef} className="flex-1 px-4 py-6 overflow-y-auto bg-[#F7F8FC]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(67, 56, 202, 0.10) 1px, transparent 0)',
        backgroundSize: '22px 22px'
      }} data-id="element-5710" data-mp="3huj8raawhveu">
          <div className="flex justify-center mb-6" data-id="element-5711" data-mp="32ybsgl2m5e4u">
            <span className="text-[11px] font-semibold tracking-wide uppercase text-slate-500 bg-white/80 backdrop-blur-sm border border-slate-200/80 px-3 py-1 rounded-full shadow-soft" data-id="element-5712" data-mp="2y7255r4jemw4">
              Today
            </span>
          </div>
          <div className="space-y-2" data-id="element-5713" data-mp="26eunqoh7j6go">
            <AnimatePresence initial={false} data-id="element-5714" data-mp="26dtbwhsdafxw">
              {messages.map(m => {
              const quoted = quotedOf(m);
              const replyBtn = <button onClick={() => {
                setReplyingTo(m);
                inputRef.current?.focus();
              }} aria-label="Reply to this message" className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400 hover:text-primary-indigo hover:border-primary-indigo/40 shadow-soft transition-[opacity,color,border-color] duration-150 ease-out" data-id="element-5715" data-mp="213yz94atfl33">
                    <ReplyIcon className="w-3.5 h-3.5" data-id="element-5716" data-mp="2a22hnrqi81qo" />
                  </button>;
              return <motion.div key={m.id} id={`msg-${m.id}`} layout initial={{
                opacity: 0,
                y: 8,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 28
              }} className={`group flex items-end gap-1.5 ${m.fromMe ? 'justify-end' : 'justify-start'}`} data-id="element-5717" data-mp="tnj6fiwiklyd">
                  {m.fromMe && replyBtn}
                  {m.share ? <div className="w-full max-w-[78%] sm:max-w-[320px] flex flex-col items-end gap-1.5" data-id="element-5718" data-mp="1nuyryphw57br">
                      {(m.text || quoted) && <div className="w-full bg-primary-indigo text-white rounded-2xl rounded-br-md px-2.5 py-2 shadow-[0_4px_14px_rgba(67,56,202,0.22)]" data-id="element-5719" data-mp="1iyrmextepxuc">
                          {quoted && <QuotedPreview message={quoted} author={authorOf(quoted)} tone="light" onJump={() => jumpTo(quoted.id)} data-id="element-5720" data-mp="7jmbx7n1av06" />}
                          {m.text && <p className="text-sm leading-relaxed px-1" data-id="element-5721" data-mp="2xu1bl0ug61zh">
                              {m.text}
                            </p>}
                        </div>}
                      {/* The customer sees the exact Discover listing card, with
                          image gallery and a Details view for the full spec. */}
                      <div className="w-full" data-id="element-5722" data-mp="3gt5dzqrhgxi6">
                        <ListingCard {...toListingCard(m.share)} data-id="element-5723" data-mp="3gru9foq7rlq0" />
                      </div>
                      <div className="flex items-center gap-1.5 pr-1" data-id="element-5724" data-mp="1hhcdvl9efc18">
                        <PackageSearchIcon className="w-3 h-3 text-slate-400" data-id="element-5725" data-mp="1mfnmjsnyjghv" />
                        <span className="text-[10px] text-slate-400" data-id="element-5726" data-mp="1eddbq4kg3pte">
                          Shared listing · {m.time}
                        </span>
                        <Ticks status={m.status} tone="dark" data-id="element-5727" data-mp="1ae4kb6cvo410" />
                      </div>
                    </div> : m.fromMe ? <div className="bg-primary-indigo text-white rounded-2xl rounded-br-md px-3.5 py-2 max-w-[70%] shadow-[0_4px_14px_rgba(67,56,202,0.22)]" data-id="element-5728" data-mp="1jcq8pi516d9p">
                      {quoted && <QuotedPreview message={quoted} author={authorOf(quoted)} tone="light" onJump={() => jumpTo(quoted.id)} data-id="element-5729" data-mp="1tbp8scbqd0l3" />}
                      <p className="text-sm leading-relaxed" data-id="element-5730" data-mp="2h0rp9gumone0">{m.text}</p>
                      <div className="flex items-center justify-end gap-1 mt-0.5 -mb-0.5" data-id="element-5731" data-mp="2gynlcywqbodq">
                        <span className="text-[10px] text-white/70" data-id="element-5732" data-mp="2c3i082b5idqp">
                          {m.time}
                        </span>
                        <Ticks status={m.status} data-id="element-5733" data-mp="2zosdzn3udjs2" />
                      </div>
                    </div> : <div className="bg-white border border-slate-200/70 text-slate-800 rounded-2xl rounded-bl-md px-3.5 py-2 max-w-[70%] shadow-soft" data-id="element-5734" data-mp="2lo7tmmia3fwg">
                      {quoted && <QuotedPreview message={quoted} author={authorOf(quoted)} tone="dark" onJump={() => jumpTo(quoted.id)} data-id="element-5735" data-mp="2bz7t2yo4ocg5" />}
                      <p className="text-sm leading-relaxed" data-id="element-5736" data-mp="27xml9rqgaoct">{m.text}</p>
                      <div className="flex justify-end mt-0.5 -mb-0.5" data-id="element-5737" data-mp="27vrzkb7cus7o">
                        <span className="text-[10px] text-slate-400" data-id="element-5738" data-mp="1t1wxbo1pg15g">
                          {m.time}
                        </span>
                      </div>
                    </div>}
                  {!m.fromMe && replyBtn}
                </motion.div>;
            })}
            </AnimatePresence>
          </div>
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 p-4 bg-white border-t border-slate-200 relative" data-id="element-5739" data-mp="24fqcz36t3kuo">
          <AnimatePresence data-id="element-5740" data-mp="1zgvoplq2l436">
            {pickerOpen && <InventorySharePicker items={pickerResults} query={slashQuery ?? ''} activeIndex={activeIndex} onHover={setActiveIndex} onSelect={shareItem} data-id="element-5741" data-mp="1uiu4gxgwqyq4" />}
          </AnimatePresence>
          <AnimatePresence initial={false} data-id="element-5742" data-mp="1lo633iege16l">
            {replyingTo && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.16,
            ease: 'easeOut'
          }} className="overflow-hidden" data-id="element-5743" data-mp="1gta6e4y9upn5">
                <div className="flex items-center gap-2 mb-2 pl-3 pr-2 py-2 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-5744" data-mp="16lnz3pooxukg">
                  <ReplyIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-5745" data-mp="dzd18msgy7rc" />
                  <div className="flex-1 min-w-0" data-id="element-5746" data-mp="ixocn1yewqbo">
                    <p className="text-[11px] font-semibold text-primary-indigo" data-id="element-5747" data-mp="iyfvrdatovl2">
                      Replying to {authorOf(replyingTo)}
                    </p>
                    <p className="text-xs text-slate-500 truncate" data-id="element-5748" data-mp="so95e8ren0mo">
                      {replyingTo.share ? `${replyingTo.share.title} · ${replyingTo.share.price}` : replyingTo.text}
                    </p>
                  </div>
                  {replyingTo.share && <img src={replyingTo.share.image} alt="" className="w-8 h-8 rounded-md object-cover flex-shrink-0" data-id="element-5749" data-mp="34nq7p01umr1m" />}
                  <button onClick={() => setReplyingTo(null)} aria-label="Cancel reply" className="w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-white transition-colors duration-150 ease-out flex-shrink-0" data-id="element-5750" data-mp="2uqbquzc0k08s">
                    <XIcon className="w-4 h-4" data-id="element-5751" data-mp="2by9iscdftsa5" />
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
          <div className="flex items-center gap-1.5 bg-white rounded-2xl pl-2 pr-1.5 py-1.5 border border-slate-200 shadow-soft focus-within:border-primary-indigo/40 focus-within:ring-4 focus-within:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-5752" data-mp="2guq7b7oju8nr">
            <button aria-label="Attach a file" className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-xl transition-colors duration-150 ease-out flex-shrink-0" data-id="element-5753" data-mp="1oaw2j82er0n6">
              <PaperclipIcon className="w-[18px] h-[18px]" strokeWidth={1.75} data-id="element-5754" data-mp="1sbocy4n0nbl1" />
            </button>
            <button aria-label="Send a photo" className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-xl transition-colors duration-150 ease-out flex-shrink-0" data-id="element-5755" data-mp="278f9851e876f">
              <ImageIcon className="w-[18px] h-[18px]" strokeWidth={1.75} data-id="element-5756" data-mp="2bzx54eok7hkj" />
            </button>
            <button onClick={openPicker} aria-label="Share an item from your inventory" className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors duration-150 ease-out flex-shrink-0 ${pickerOpen ? 'text-primary-indigo bg-indigo-50' : 'text-slate-400 hover:text-primary-indigo hover:bg-indigo-50'}`} data-id="element-5757" data-mp="2pzpvg8w6bvrz">
              <PackageSearchIcon className="w-[18px] h-[18px]" strokeWidth={1.75} data-id="element-5758" data-mp="1mmpuxrx184sk" />
            </button>
            <input ref={inputRef} type="text" value={draft} onChange={e => {
            setDraft(e.target.value);
            syncSlashCommand(e.target.value);
          }} onKeyDown={handleComposerKeyDown} placeholder="Type a message, or / to share inventory…" className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400 py-2 px-1" data-id="element-5759" data-mp="1wevhun8ffw0w" />
            <button onClick={send} disabled={!draft.trim()} aria-label="Send message" className="w-10 h-10 flex items-center justify-center bg-primary-indigo text-white rounded-full shadow-[0_4px_14px_rgba(67,56,202,0.28)] hover:bg-indigo-700 active:scale-95 transition-[background-color,transform,opacity] duration-150 ease-out flex-shrink-0 disabled:opacity-30 disabled:shadow-none disabled:cursor-not-allowed" data-id="element-5760" data-mp="g15b18pxatnk">
              <ArrowUpIcon className="w-[18px] h-[18px]" strokeWidth={2.5} data-id="element-5761" data-mp="3uwxmf1mrq4qm" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}